import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RotateCcw, ShieldCheck } from "lucide-react";
import { SectionHeader, Tile } from "../../components/Primitives";
import ModuleRow from "../../components/modules/ModuleRow";
import ConfirmChangeModal from "../../components/modules/ConfirmChangeModal";
import VisibilityHistory from "../../components/modules/VisibilityHistory";
import { actorName } from "../../components/modules/util";
import { relTime } from "../../components/apikeys/shared";
import { OriginSummary } from "../../lib/origin.jsx";
import { useRole } from "../../lib/role.jsx";
import { useModules } from "../../lib/modules.jsx";
import { getModuleCatalog, getModuleHistory, setModuleVisibility, applyPilotPreset } from "../../lib/pilotApi";

const SECTIONS = ["Executive", "AI Layer", "Digital Twin", "Governance", "Admin Console"];

export default function ModuleVisibility() {
  const { user } = useRole();
  const { refresh: refreshModules } = useModules();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);
  const [change, setChange] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([getModuleCatalog(user), getModuleHistory(user, 100)]);
      setData(c); setEvents(h.events); setError(null);
    } catch (e) { setError(e.message); }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const labelOf = useMemo(() => {
    const m = Object.fromEntries((data?.modules || []).map(x => [x.path, x.label]));
    return (p) => m[p] || p;
  }, [data]);

  const presetDiff = useMemo(() => {
    if (!data) return null;
    const cur = new Set(data.hidden), want = new Set(data.pilot_hidden);
    return { kind: "preset", hide: data.pilot_hidden.filter(p => !cur.has(p)).map(labelOf),
             show: data.hidden.filter(p => !want.has(p)).map(labelOf) };
  }, [data, labelOf]);
  const presetApplied = presetDiff && !presetDiff.hide.length && !presetDiff.show.length;

  async function confirm(reason) {
    if (change.kind === "preset") await applyPilotPreset(reason, user);
    else await setModuleVisibility(change.mod.path, change.visible, reason, user);
    await Promise.all([load(), refreshModules()]);
  }

  const visibleCount = data ? data.modules.filter(m => m.visible).length : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="module-visibility-page">
      <SectionHeader eyebrow="Admin Console · Pilot Focus" title="Module Visibility"
        description="Show or hide whole modules for every user at once. Hiding never grants access — each role still sees only what its permissions allow."
        action={data && (
          <button data-testid="apply-pilot-preset-btn" disabled={presetApplied} onClick={() => setChange(presetDiff)}
                  title={presetApplied ? "The pilot module set is already applied" : undefined}
                  className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold bg-primary text-primary-fg rounded-full hover:opacity-90 transition-opacity disabled:opacity-40">
            <RotateCcw size={13} /> {presetApplied ? "Pilot set applied" : "Apply pilot module set"}
          </button>
        )} />

      <OriginSummary variant="system" />

      {error && <div data-testid="module-visibility-error" className="text-[12px] text-critical border border-critical/30 bg-critical/5 rounded-sm p-3">{error}</div>}
      {!data && !error && <div className="flex items-center gap-2 text-muted text-sm"><Loader2 size={14} className="animate-spin" /> Loading modules…</div>}

      {data && (
        <>
          <div data-testid="module-visibility-status" className="text-[11.5px] border border-success/30 bg-success/5 text-success rounded-sm px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={13} /> IT Head · changes apply to everyone and are recorded with your reason.</span>
            <span className="font-mono-tabular text-fg/80" data-testid="module-visible-count">{visibleCount} of {data.modules.length} modules visible</span>
            {data.updated_at && <span className="text-muted">Last change · {actorName(data.updated_by)} · {relTime(data.updated_at)}</span>}
          </div>
          {SECTIONS.map(sec => {
            const mods = data.modules.filter(m => m.section === sec);
            return (
              <Tile key={sec} dense testid={`module-section-${sec.toLowerCase().replace(/\s+/g, "-")}`}
                    title={sec} action={<span className="text-[10px] text-subtle font-mono-tabular">{mods.filter(m => m.visible).length}/{mods.length} visible</span>}>
                {mods.map(m => <ModuleRow key={m.path} mod={m} labelOf={labelOf}
                                          onToggle={(mod) => setChange({ kind: "toggle", mod, visible: !mod.visible })} />)}
              </Tile>
            );
          })}
          <VisibilityHistory events={events} />
        </>
      )}

      {change && <ConfirmChangeModal change={change} labelOf={labelOf} onClose={() => setChange(null)} onConfirm={confirm} />}
    </div>
  );
}
